//#region Imports and Global Definitions
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StyleSheet, View, Modal, SafeAreaView, Image, Text, Platform, useWindowDimensions, Pressable, FlatList } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import { createCommonStyles } from '../../../styles/commonStyles';
import { ActivityIndicator, Card } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import { useTableHelper } from '../../../hooks/useTableHelper';
import { useTranslation } from 'react-i18next';
import { useTraining } from '../../../hooks/useTraining';
import { DEFAULTPROFILE } from '../../../img/DefaultProfile';
import { generateUniqueID } from '../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions';
import { ScrollView } from 'react-native-gesture-handler';
//#endregion

const ModulesListView = React.memo(({ item, index, styles, handleEditPress, handleDelete,
    openAttendanceModal, openFacilitatorsModal
}) => {
    return (
        <SafeAreaView>
            <SafeAreaView style={styles.row}>
                <View style={[styles.item]}>
                    <CustomInput
                        value={item.description}
                        setValue={(e) => { handleEditPress(index, item, 'description', e) }}
                        maxLen={50}
                        type="MAININPUT"
                    />
                </View>
                <View style={[styles.item]}>
                    <CustomInput
                        value={item.duration?.toString()}
                        setValue={(e) => { handleEditPress(index, item, 'duration', e) }}
                        maxLen={10}
                        type="MAININPUT"
                    />
                </View>
                <CustomButton
                    iconName='trash-outline'
                    onPress={() => { handleDelete(item, index, 'rowguid') }}
                    show='HOME_BELL'
                    type='HOME_BELL'
                    textTop='2'
                    sz={30} />
                {item.rowguid && <>
                <CustomButton
                    iconName='list-outline'
                    onPress={() => { openAttendanceModal(item) }}
                    show='HOME_BELL'
                    type='HOME_BELL'
                    textTop='2'
                    sz={30} />
                <CustomButton
                    iconName='people-outline'
                    onPress={() => { openFacilitatorsModal(item) }}
                    show='HOME_BELL'
                    type='HOME_BELL'
                    textTop='2'
                    sz={30} />
                </>    
                }
            </SafeAreaView>
        </SafeAreaView>
    );
});


const FacilitatorsList = React.memo(({ item, index, styles, common, employeeOptions,
    handleEditPress, handleDelete
 }) => {
    return (
        <Card style={[common.cardstyle, { margin: 10 }]} >
            <Card.Content>
                <SafeAreaView>
                    <SafeAreaView style={styles.row}>
                        <View style={[styles.item]}>
                            <CustomDropDown
                                defValue={item.employeeid}
                                options={employeeOptions}
                                value={item.employeeid}
                                setValue={(e) => { handleEditPress(index, item, 'employeeid', e)}}
                                type="ASSESSMENT"
                            />
                        </View>
                        <CustomButton
                            iconName='trash-outline'
                            onPress={() => { handleDelete(item, index, 'rowguid') }}
                            show='HOME_BELL'
                            type='HOME_BELL'
                            textTop='2'
                            sz={30} />
                    </SafeAreaView>
                </SafeAreaView>
            </Card.Content>
        </Card>
    );
});

const HRAdminTrainingModulesScreen = (props) => {
    //#region Props
    const { isEdit, setIsEdit, trnUI, employeeOptions, trainings } = props
    //#endregion

    //#region Const Definitions
    const ref = useRef(null);
    const { t, i18n } = useTranslation();
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const [isLoading, setIsLoading] = useState(true);

    const { loadModules, loadParticipantsLeft, loadAttendees, saveAttendees,
        removeAttendees, saveModule, deleteModules, loadFacilitators, saveFacilitators,
        deleteFacilitators, copyModules
    } = useTraining();
    const [refreshAfterSave, setRefreshAfterSave] = useState(false);
    const { rowEdit, rowDelete, rowAdd } = useTableHelper();

    const [facilitators, setFacilitators] = useState([]);
    const [modulesData, setModulesData] = useState([]);
    const [changes, setChanges] = useState({});
    const [changesF, setChangesF] = useState({});
    const [insertData, setInsertData] = useState([]);
    const [insertFData, setInsertFData] = useState([]);
    const [deletedItems, setDeletedItems] = useState([]);
    const [deletedFItems, setDeletedFItems] = useState([]);

    // Copy Modules State Variables
    const [trnOptions, setTRNOptions] = useState([]);
    const [fromUI, setFromUI] = useState(null);
    const [copyModalVisible, setCopyModalVisible] = useState(false);

    const openCopyModal = () => {
        setCopyModalVisible(true);
    };

    const closeCopyModal = useCallback(() => {
        setErrorMessage('');
        setCopyModalVisible(false);
    }, []);

    // States for attendance modal
    const [modalVisible, setModalVisible] = useState(false);
    const [modalFacilitatorsVisible, setModalFacilitatorsVisible] = useState(false);
    const [selectedModule, setSelectedModule] = useState(null);
    const [participantsLeft, setParticipantsLeft] = useState([]);
    const [attendees, setAttendees] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    //#endregion

    useEffect(() => {
        setIsLoading(true);
        let options = trainings.map(training => ({
            value: training.trnUI,
            label: training.title
        }))
        setTRNOptions(options);
        if(isEdit){
            loadModules(trnUI).then((mod) => {
                setModulesData(mod);
                setRefreshAfterSave(false);
                setChanges({});
                setInsertData([]);
                setDeletedItems([]);
                setIsLoading(false);
            })
                .catch(() => {
                    setRefreshAfterSave(false);
                    setIsLoading(false);
                });
        }
        setRefreshAfterSave(false);
        setIsLoading(false);
    }, [refreshAfterSave])

    //#region Components
    const renderData = (item, index) => {
        return (
            <ModulesListView
                item={item}
                index={index}
                styles={styles}
                handleEditPress={handleEditPress}
                handleDelete={handleDelete}
                openAttendanceModal={openAttendanceModal}
                openFacilitatorsModal={openFacilitatorsModal}
            />
        )
    }

    const renderFacilitators = (item, index) => {
        return (
            <FacilitatorsList
                item={item}
                index={index}
                styles={styles}
                common={common}
                employeeOptions={employeeOptions}
                handleEditPress={handleEditFPress}
                handleDelete={handleDeleteF}
            />
        )
    }

    const renderModules = useCallback((item, index) => {
        return (
            <Card
                style={[common.cardstyle, { margin: 10, zIndex: -1 }]} >
                <Card.Content>
                    {renderData(item, index)}
                </Card.Content>
            </Card >
        )
    }, [modulesData]);
    //#endregion

    //#region Facilitators
    const openFacilitatorsModal = (item) => {
        setSelectedModule(item);
        setErrorMessage('');
        setModalFacilitatorsVisible(true);

        loadFacilitators(item.rowguid).then((res) => {
            setFacilitators(res)
        }).catch((err) => {

        });
    }
    //#endregion

    //#region Attendance
    const openAttendanceModal = (item) => {
        setSelectedModule(item);
        setErrorMessage('');
        Promise.all([
            loadParticipantsLeft(trnUI, item.rowguid),
            loadAttendees(trnUI, item.rowguid)
        ]).then(([leftRes, attendeesRes]) => {
            setParticipantsLeft(leftRes || []);
            setAttendees(attendeesRes || []);
            setModalVisible(true);
        }).catch((err) => {
            setErrorMessage(t('common:errLoadingData'));
            setParticipantsLeft([]);
            setAttendees([]);
            setModalVisible(true);
        });
    };

    const closeModal = () => {
        setModalVisible(false);
        setSelectedModule(null);
        setParticipantsLeft([]);
        setAttendees([]);
        setErrorMessage('');
    };

    // Move one participant from left to right
    const moveOneToRight = (itemToMove = null) => {
        if (participantsLeft.length === 0) return;

        if (itemToMove) {
            const index = participantsLeft.findIndex(item => item.employeeID === itemToMove.employeeID);
            if (index === -1) return;

            const newParticipantsLeft = [...participantsLeft];
            newParticipantsLeft.splice(index, 1);
            setParticipantsLeft(newParticipantsLeft);

            setAttendees(prev => [...prev, itemToMove]);
        } else {
            // Move first item by default
            const [first, ...rest] = participantsLeft;
            setParticipantsLeft(rest);
            setAttendees(prev => [...prev, first]);
        }
    };

    const moveOneToLeft = (itemToMove = null) => {
        if (attendees.length === 0) return;

        if (itemToMove) {
            const index = attendees.findIndex(item => item.employeeID === itemToMove.employeeID);
            if (index === -1) return;

            const newAttendees = [...attendees];
            newAttendees.splice(index, 1);
            setAttendees(newAttendees);

            setParticipantsLeft(prev => [...prev, itemToMove]);
        } else {
            // Move first item by default
            const [first, ...rest] = attendees;
            setAttendees(rest);
            setParticipantsLeft(prev => [...prev, first]);
        }
    };

    // Move all participants from left to right
    const moveAllToRight = () => {
        if (participantsLeft.length === 0) return;
        setAttendees(prev => [...prev, ...participantsLeft]);
        setParticipantsLeft([]);
    };

    // Move all attendees from right to left
    const moveAllToLeft = () => {
        if (attendees.length === 0) return;
        setParticipantsLeft(prev => [...prev, ...attendees]);
        setAttendees([]);
    };
    //#endregion

    //#region Render functions for lists
    const renderParticipantsRightItem = ({ item }) => (
        <Pressable
            onPress={() => { moveOneToLeft(item) }}
        >
            <SafeAreaView style={styles.employeeContainer}>
                <Image
                    style={styles.employeePhotograph}
                    source={{
                        uri: `data:image/png;base64,${item.photograph || DEFAULTPROFILE}`,
                    }}
                />
                <CustomLabel value={item.name} type="IMAGE_TEXT" />
            </SafeAreaView>
        </Pressable>
    );

    const renderParticipantsLeftItem = ({ item }) => (
        <Pressable
            onPress={() => { moveOneToRight(item) }}
        >
            <SafeAreaView style={styles.employeeContainer}>
                <Image
                    style={styles.employeePhotograph}
                    source={{
                        uri: `data:image/png;base64,${item.photograph || DEFAULTPROFILE}`,
                    }}
                />
                <CustomLabel value={item.name} type="IMAGE_TEXT" />
            </SafeAreaView>
        </Pressable>
    );
    //#endregion

    //#region Handlers
    const handleEditPress = useCallback((index, item, key, value) => {
        if (item[key] === value) return;
        rowEdit(index, item, key, value, changes, setChanges, modulesData, setModulesData)
    }, []);

    const handleEditFPress = useCallback((index, item, key, value) => {
        if (item[key] === value) return;
        rowEdit(index, item, key, value, changesF, setChangesF, facilitators, setFacilitators)
    }, []);

    const handleDelete = (item, index, attribute) => {
        rowDelete(item, index, attribute, changes, setChanges, modulesData, setModulesData, setDeletedItems, ref)
    }

    const handleDeleteF = (item, index, attribute) => {
        rowDelete(item, index, attribute, changesF, setChangesF, facilitators, setFacilitators, setDeletedFItems, ref)
    }

    // Update handleAddNew to add new module item with correct keys
    const handleAddNew = () => {
        let temp = [...modulesData];
        if (modulesData.length === 0)
            temp = {
                description: "",
                duration: "",
                rowguid: generateUniqueID(),
                trnUI: trnUI
            }
        rowAdd(temp, setModulesData, ['rowguid'], [], ref, t, [], [], undefined, undefined, insertData, setInsertData, id = 'rowguid',);
    }

    const handleAddNewFacilitator = () => {
        let temp = [...facilitators];
        if (facilitators.length === 0)
            temp = {
                employeeid: "",
                rowguid: generateUniqueID(),
                moduleUI: selectedModule.moduleUI
            }
        rowAdd(temp, setFacilitators, ['rowguid'], [], ref, t, [], [], undefined, undefined, insertFData, setInsertFData, id = 'rowguid',);
    }

    const removeFromChangesAfterSave = () => {
        setChanges([])
        setChangesF([])
        setDeletedItems([])
        setDeletedFItems([])
    }

    //#region SaveModule
    const handleSave = async () => {
        if (Object.entries(changes).length === 0 && deletedItems.length === 0 && insertData.length === 0) {
            showMessage({
                message: t('common:noChanges'),
                type: "warning",
                duration: 3000,
            });
            return;
        }

        // Validate required fields
        for (const mod of modulesData) {
            if (!mod.description || mod.description.trim() === '') {
                showMessage({
                    message: t('common:descEmpty'),
                    type: "danger",
                    duration: 3000,
                });
                return;
            }
            if (!mod.duration || isNaN(Number(mod.duration))) {
                showMessage({
                    message: t('common:invalidDuration'),
                    type: "danger",
                    duration: 3000,
                });
                return;
            }
        }

        setIsLoading(true);

        // Prepare data for insert and update
        const dataForInsert = [];
        const dataForUpdate = [];
        const insertSuccess = true;
        const updateSuccess = true;
        const deleteSuccess = true;
        const deleteErrorMsg = "";
        const promises = [];

        modulesData.forEach(mod => {
            if (insertData.includes(mod.rowguid)) {
                dataForInsert.push(mod);
            } else {
                dataForUpdate.push(mod);
            }
        });

        dataForInsert.forEach(item => {
            item.trnUI = trnUI;
            item.rowguid = item.rowguid || generateUniqueID();
        });

        dataForUpdate.forEach(item => {
            item.trnUI = trnUI;
        });

        if (dataForInsert.length > 0) {
            promises.push(
                saveModule(dataForInsert)
                    .then(res => {
                        if (res !== 200)
                            insertSuccess = false;
                    })
            );
        }

        if (dataForUpdate.length > 0) {
            promises.push(
                saveModule(dataForUpdate)
                    .then(res => {
                        if (res !== 200)
                            updateSuccess = false;
                    })
            );
        }

        if (deletedItems.length > 0) {
            promises.push(
                deleteModules(deletedItems)
                    .then(res => {
                        if (res !== 200){
                            deleteErrorMsg = res;
                            deleteSuccess = false;
                        }
                    })
            );
        }

        try {
            await Promise.all(promises);
            if(!deleteSuccess){
                showMessage({
                    message: deleteErrorMsg,
                    type: "danger",
                    duration: 3000,
                });
            }
            if (updateSuccess && insertSuccess && deleteSuccess) {
                showMessage({
                    message: t('common:allOperationsSuccMess'),
                    type: "success",
                    duration: 3000,
                });
                setRefreshAfterSave(true);
                removeFromChangesAfterSave();
            } else {
                showMessage({
                    message: t('common:saveFailed'),
                    type: "danger",
                    duration: 3000,
                });
                setRefreshAfterSave(true);
                removeFromChangesAfterSave();
            }
        } catch (error) {
            showMessage({
                message: t('common:saveFailed'),
                type: "danger",
                duration: 3000,
            });
            setRefreshAfterSave(true);
            removeFromChangesAfterSave();
        } finally {
            setIsLoading(false);
        }
    };
    //#endregion

    //#region Save Attend
    const saveAttendance = () => {
        const attendeesToSave = attendees.map(att => ({
            ...att,
            moduleUI: selectedModule?.rowguid,
            trainingnoUI: trnUI
        }));

        const attendeesToRemove = participantsLeft.map(att => ({
            ...att,
            moduleUI: selectedModule?.rowguid,
            trainingnoUI: trnUI
        }));

        saveAttendees(attendeesToSave).then((res) => {
            if (res === 200)
                removeAttendees(attendeesToRemove).then((res) => {
                    if (res === 200)
                        showMessage({
                            message: t('common:saveSuccess'),
                            type: "success",
                            duration: 3000,
                        });
                    closeModal();
                }).catch((err) => {
                    showMessage({
                        message: t('common:saveFailed'),
                        type: "danger",
                        duration: 3000,
                    });
                    closeModal();
                })
        }).catch((res) => {
            showMessage({
                message: t('common:saveFailed'),
                type: "danger",
                duration: 3000,
            });
            closeModal();
        })
    }
    //#endregion

    //#region SaveFacil
    const SaveFacilitators = async () => {
        if (Object.entries(changesF).length === 0 && deletedFItems.length === 0 && insertFData.length === 0) {
            setModalFacilitatorsVisible(false);
            showMessage({
                message: t('common:noChanges'),
                type: "warning",
                duration: 3000,
            });
            return;
        }

        // Validate required fields
        for (const fac of facilitators) {
            if (!fac.employeeid) {
                setErrorMessage(t('common:employeeEmpty'));
                return;
            }
        }

        setIsLoading(true);
        const facilitatorsToSave = facilitators.map(faci => ({
            ...faci,
            rowguid: faci.rowguid || generateUniqueID(),
            moduleUI: selectedModule?.rowguid,
            trnUI: trnUI
        }));
        const promises = [];
        const success = true;
        const deleteSuccess = true;

        promises.push(
            saveFacilitators(facilitatorsToSave)
            .then(res => {
                if (res !== 200)
                    success = false;
            })
        );

        if (deletedFItems.length > 0) {
            promises.push(
                deleteFacilitators(deletedFItems)
                    .then(res => {
                        if (res !== 200)
                            deleteSuccess = false;
                    })
            );
        }

        try {
            await Promise.all(promises);
            if (success && deleteSuccess) {
                setModalFacilitatorsVisible(false);
                showMessage({
                    message: t('common:allOperationsSuccMess'),
                    type: "success",
                    duration: 3000,
                });
                setRefreshAfterSave(true);
                removeFromChangesAfterSave();
            } else {
                setErrorMessage(t('common:saveFailed'))
            }
        } catch (error) {
            setErrorMessage(t('common:saveFailed'))
        } finally {
            setIsLoading(false);
        }
       
    }
    //#endregion

    //#region CopyModules
    const copyModule = () => {
        closeCopyModal();
        copyModules(fromUI, trnUI).then((res) => {
            if (res === 200) {
                showMessage({
                    message: t('common:copyPartSuccess'),
                    type: "success",
                    duration: 3000,
                });
                setRefreshAfterSave(true);
            }
            setIsLoading(false)
        }).catch((error) => {
            showMessage({
                message: t('common:copyPartError'),
                type: "danger",
                duration: 3000,
            })
        })
    }
             

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.innerView}>
            {/* Left side: Buttons */}
            <SafeAreaView style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 10, marginBottom: 10 }}>
                <SafeAreaView>
                </SafeAreaView>
                <SafeAreaView style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <CustomButton
                        iconName='copy-outline'
                        onPress={openCopyModal}
                        tooltipText={"Copy Modules"}
                        textTop='3'
                        type="PART"
                        show="PART"
                        sz={25}
                        style={{ marginRight: 8 }}
                    />
                </SafeAreaView>
            </SafeAreaView>
            <SafeAreaView style={[styles.buttons, styles.functionalityButtons, {zIndex:-1}]}>
                <CustomButton
                    text={t('common:new')}
                    onPress={() => { handleAddNew() }}
                    type='BUTTON_ADD'
                    textTop='0'
                    sz={15}
                />
                {isEdit && <CustomButton
                    text={t('common:save')}
                    onPress={() => { handleSave() }}
                    type='BUTTON_SAVE'
                    textTop='0'
                    sz={15}
                    isGradient={true}
                />}
                <CustomButton
                    text={t('common:cancel')}
                    onPress={() => { setRefreshAfterSave(true) }}
                    type="BUTTON_CANCEL"
                    textTop='0'
                    sz={15}
                />
            </SafeAreaView>
            <FlatList
                data={modulesData}
                keyExtractor={(item) => item.rowguid}
                scrollEnabled={true}
                initialNumToRender={4}
                renderItem={({ item, index }) =>
                    renderModules(item, index)
                }
                ListHeaderComponent={
                    <SafeAreaView style={common.listTitle}>
                        <SafeAreaView style={[styles.rowHeaders]}>
                            <CustomLabel
                                value={t('common:description')}
                                type='SUBTITLE' />
                            <CustomLabel
                                value={t('common:duration')}
                                type='SUBTITLE' />
                        </SafeAreaView>
                        <SafeAreaView style={common.divider} />
                    </SafeAreaView>
                }
                ListEmptyComponent={
                    isLoading ?
                        <ActivityIndicator animating={true} color={colors.blue} />
                        :
                        <CustomLabel
                            value={t("common:NoDataPreview")}
                            type="NO_FUNCTIONS"
                        />
                }
            />
            {/* Attendance Modal */}
            <Modal
                animationType="slide"
                transparent={true}
                visible={modalVisible}
                onRequestClose={() => { setModalVisible(false) }}
            >
                <SafeAreaView style={styles.modalOverlay}>
                    <SafeAreaView style={styles.modalContainer}>
                        <SafeAreaView style={{ padding: 20 }}>
                            <CustomLabel
                                value={`${selectedModule?.description || ''} - Attendance`}
                                type="TITLE"
                                style={{ textAlign: 'left', margin: 10 }}
                            />
                        </SafeAreaView>

                        <SafeAreaView style={{ paddingHorizontal: 20 }}>
                            {errorMessage ? (
                                <View style={styles.errorMessageContainer}>
                                    <Text style={styles.errorMessageText}>{errorMessage}</Text>
                                </View>
                            ) : null}
                        </SafeAreaView>

                        <SafeAreaView style={styles.attendanceContent}>
                            {/* Left Column */}
                            <SafeAreaView style={[common.cardstyle, styles.columnLeft]}>
                                <SafeAreaView style={{ height: '10%' }}>
                                    <CustomLabel
                                        value={t('common:participantsLeft')}
                                        type='SUBTITLE'
                                    />
                                    <SafeAreaView style={common.divider} />
                                </SafeAreaView>
                                <FlatList
                                    data={participantsLeft}
                                    keyExtractor={(item, index) => item.id?.toString() || index.toString()}
                                    renderItem={renderParticipantsLeftItem}
                                />
                            </SafeAreaView>

                            {/* Middle Column */}
                            <SafeAreaView style={[styles.columnMiddle]}>
                                <CustomButton
                                    iconName='play-forward-outline'
                                    onPress={moveAllToRight}
                                    show='HOME_BELL'
                                    type='HOME_BELL'
                                    textTop='2'
                                    sz={25}
                                />
                                <SafeAreaView style={{ marginVertical: 20 }} />
                                <CustomButton
                                    iconName='play-back-outline'
                                    onPress={moveAllToLeft}
                                    show='HOME_BELL'
                                    type='HOME_BELL'
                                    textTop='2'
                                    sz={25}
                                />
                            </SafeAreaView>

                            {/* Right Column */}
                            <SafeAreaView style={[common.cardstyle, styles.columnRight]}>
                                <SafeAreaView style={{ height: '10%' }}>
                                    <CustomLabel
                                        value={t('common:attendees')}
                                        type='SUBTITLE'
                                    />
                                    <SafeAreaView style={common.divider} />
                                </SafeAreaView>
                                <FlatList
                                    data={attendees}
                                    keyExtractor={(item, index) => item.id?.toString() || index.toString()}
                                    renderItem={renderParticipantsRightItem}
                                />
                            </SafeAreaView>
                        </SafeAreaView>
                        {/* Bottom Buttons */}
                        <SafeAreaView style={styles.modalButtonsContainer}>
                            <CustomButton
                                text={t('common:cancel')}
                                onPress={closeModal}
                                type="BUTTON_CANCEL"
                                sz={15}
                                style={{ marginRight: 10 }}
                            />
                            <CustomButton
                                text={t('common:save')}
                                onPress={() => { saveAttendance() }}
                                type="BUTTON_SAVE"
                                sz={15}
                                isGradient={true}
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                </SafeAreaView>
            </Modal>
            {/* Attendance Modal */}
            <Modal
                animationType="slide"
                transparent={true}
                visible={modalFacilitatorsVisible}
                onRequestClose={() => { setModalFacilitatorsVisible(false) }}
            >
                <SafeAreaView style={styles.modalOverlay}>
                    <SafeAreaView style={styles.modalContainer}>
                        <SafeAreaView style={{ padding: 20 }}>
                            <CustomLabel
                                value={`${selectedModule?.description || ''} - Facilitators`}
                                type="TITLE"
                                style={{ textAlign: 'left', margin: 10 }}
                            />
                        </SafeAreaView>

                        <SafeAreaView style={{ paddingHorizontal: 20 }}>
                            {errorMessage ? (
                                <View style={styles.errorMessageContainer}>
                                    <Text style={styles.errorMessageText}>{errorMessage}</Text>
                                </View>
                            ) : null}
                        </SafeAreaView>

                        <FlatList
                            data={facilitators}
                            keyExtractor={(item) => item.rowguid}
                            scrollEnabled={true}
                            initialNumToRender={4}
                            renderItem={({ item, index }) =>
                                renderFacilitators(item, index)
                            }
                            ListHeaderComponent={
                                <SafeAreaView style={common.listTitle}>
                                    <SafeAreaView style={[styles.rowHeaders]}>
                                        <CustomLabel
                                            value={t('common:facilitator')}
                                            type='SUBTITLE' />
                                    </SafeAreaView>
                                    <SafeAreaView style={common.divider} />
                                </SafeAreaView>
                            }
                        />

                        {/* Bottom Buttons */}
                        <SafeAreaView style={styles.modalButtonsContainer}>
                           <CustomButton
                                text={t('common:new')}
                                onPress={() => { handleAddNewFacilitator() }}
                                type='BUTTON_ADD'
                                textTop='0'
                                sz={15}
                            />
                            <CustomButton
                                text={t('common:cancel')}
                                onPress={() => { setModalFacilitatorsVisible(false) }}
                                type="BUTTON_CANCEL"
                                sz={15}
                                style={{ marginRight: 10 }}
                            />
                            <CustomButton
                                text={t('common:save')}
                                onPress={() => {
                                    SaveFacilitators();
                                }}
                                type="BUTTON_SAVE"
                                sz={15}
                                isGradient={true}
                            />
                        </SafeAreaView>

                    </SafeAreaView>
                </SafeAreaView>
            </Modal>
            {/* Modal To copy Participants */}
            <Modal
                animationType="slide"
                transparent={true}
                visible={copyModalVisible}
                onRequestClose={closeCopyModal}
                onBackdropPress={closeCopyModal}
            >
                <SafeAreaView style={styles.modalOverlay2}>
                    <SafeAreaView style={styles.modalContainer2}>
                        <SafeAreaView style={{ padding: 20 }}>
                            <CustomLabel value="Copy Modules" type="TITLE" style={{ textAlign: 'left', margin: 10 }} />
                        </SafeAreaView>

                        <SafeAreaView style={{ paddingHorizontal: 20 }}>
                            {errorMessage ? (
                                <View style={styles.errorMessageContainer}>
                                    <Text style={styles.errorMessageText}>{errorMessage}</Text>
                                </View>
                            ) : null}
                        </SafeAreaView>

                        <ScrollView style={{ flexDirection: 'row', padding: 20 }}>
                            <CustomLabel
                                value={"Copy Modules From" + ":"}
                                type='EDIT_EMPLOYEE_HEADER'
                            />
                            <CustomDropDown
                                type="EDIT_EMPLOYEE"
                                options={trnOptions}
                                setValue={(val) => { setFromUI(val) }}
                                value={fromUI}
                                defValue={fromUI}
                            />
                        </ScrollView>
                        <SafeAreaView style={{ flexDirection: 'row', justifyContent: "center" }}>
                            <CustomButton text="Copy" onPress={copyModule} type="BUTTON_SAVE" isGradient={true} />
                            <CustomButton text="Cancel" onPress={closeCopyModal} type="BUTTON_CANCEL" />
                        </SafeAreaView>
                    </SafeAreaView>
                </SafeAreaView>
            </Modal>
        </SafeAreaView>
    );
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
    },
    subMain: {
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        width: '100%'
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    cardStyleFunctions: {
        margin: 10,
        borderRadius: 5,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    item: {
        flex: 1,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    selectedButton: {
        margin: 10,
        borderRadius: 5,
        borderWidth: 2,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        borderColor: colors.buttons_submit,
    },
    backButton: {
        alignSelf: 'flex-end',
        margin: 0
    },
    innerView: {
        flex: 1,
    },
    functionsView: {
        flexDirection: 'row',
        height: '100%'
    },
    fullHeight: {
        height: '95%',
    },
    fullWidth: {
        height: '85%',
    },
    servLinesView: {
        width: '85%',
        height: '100%',
    },
    functionalityButtons: {
        justifyContent: 'flex-end'
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },
    listTitle: {
        flex: 1,
        marginVertical: 10,
    },
    rowHeaders: {
        flexDirection: "row",
        marginHorizontal: '1%',
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContainer: {
        backgroundColor: colors.background,
        borderRadius: 10,
        width: '80%',
        height: '70%',
        padding: 20,
    },
    errorMessageContainer: {
        backgroundColor: colors.validation_error,
        padding: 10,
        borderRadius: 5,
        marginBottom: 10,
    },
    errorMessageText: {
        color: 'white',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    attendanceContent: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    columnLeft: {
        flex: 4,
        paddingRight: 5,
    },
    columnMiddle: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: '5%'
    },
    columnRight: {
        flex: 4,
        marginLeft: 5,
    },
    listItem: {
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: colors.card_line,
    },
    modalButtonsContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        paddingHorizontal: 20,
        paddingVertical: 15,
        marginTop: 10,
    },
    employeeContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        marginVertical: 3,
    },
    employeePhotograph: {
        width: 30,
        height: 30,
        marginLeft: 10,
        borderRadius: 100,
        borderWidth: 1,
        verticalAlign: 'middle',
    },
    modalOverlay2: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer2: {
        maxWidth: '95%',
        maxHeight: '90%',
        backgroundColor: 'white',
        borderRadius: 10,
        padding: 20,
    },
});
//#endregion

export default HRAdminTrainingModulesScreen;